import { useState } from "react";
import { X, TrendingUp, TrendingDown, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { SimStock, SimHolding } from "@/hooks/useSimulator";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/errors";

interface Props {
  ticker: string;
  stock: SimStock;
  price: number;
  cash: number;
  holding?: SimHolding;
  onBuy: (ticker: string, shares: number) => Promise<void>;
  onSell: (ticker: string, shares: number) => Promise<void>;
  onClose: () => void;
}

const TradeModal = ({ ticker, stock, price, cash, holding, onBuy, onSell, onClose }: Props) => {
  const [mode, setMode] = useState<"buy" | "sell">("buy");
  const [shares, setShares] = useState(1);
  const [loading, setLoading] = useState(false);

  const maxBuy = Math.floor(cash / price);
  const maxSell = holding?.shares || 0;
  const total = price * shares;
  const gain = holding ? ((price - holding.avgCost) / holding.avgCost) * 100 : 0;

  const handleTrade = async () => {
    if (shares <= 0) return;
    setLoading(true);
    try {
      if (mode === "buy") {
        await onBuy(ticker, shares);
        toast.success(`Bought ${shares} shares of ${ticker}`);
      } else {
        await onSell(ticker, shares);
        toast.success(`Sold ${shares} shares of ${ticker}`);
      }
      onClose();
    } catch (err: unknown) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background/80 backdrop-blur-sm" onClick={onClose}>
      <div
        className="glass-card glow-border w-full max-w-sm mx-4 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-heading font-bold text-lg text-foreground">{ticker}</h3>
            <p className="text-muted-foreground text-xs">{stock.company_name} · {stock.sector}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Price info */}
        <div className="flex items-center gap-4 mb-5">
          <div>
            <p className="text-muted-foreground text-xs">Current Price</p>
            <p className="text-foreground font-heading font-bold text-xl">${price.toFixed(2)}</p>
          </div>
          {holding && (
            <div>
              <p className="text-muted-foreground text-xs">Your Position</p>
              <p className="text-accent font-heading font-bold">
                {holding.shares} shares
                <span className={`text-xs ml-1 ${gain >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  ({gain >= 0 ? "+" : ""}{gain.toFixed(1)}%)
                </span>
              </p>
            </div>
          )}
        </div>

        {/* Buy/Sell toggle */}
        <div className="flex gap-1 bg-muted/30 rounded-lg p-1 mb-5">
          <button
            onClick={() => { setMode("buy"); setShares(1); }}
            className={`flex-1 py-1.5 rounded-md text-xs font-heading font-semibold transition-all ${
              mode === "buy" ? "bg-emerald-400/20 text-emerald-400" : "text-muted-foreground"
            }`}
          >
            <TrendingUp className="w-3.5 h-3.5 inline mr-1" /> Buy
          </button>
          <button
            onClick={() => { setMode("sell"); setShares(1); }}
            className={`flex-1 py-1.5 rounded-md text-xs font-heading font-semibold transition-all ${
              mode === "sell" ? "bg-red-400/20 text-red-400" : "text-muted-foreground"
            }`}
            disabled={!holding || holding.shares === 0}
          >
            <TrendingDown className="w-3.5 h-3.5 inline mr-1" /> Sell
          </button>
        </div>

        {/* Shares input */}
        <div className="mb-4">
          <label className="text-muted-foreground text-xs mb-1 block">Shares</label>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShares(Math.max(1, shares - 1))}
              className="w-8 h-8 rounded-lg border border-border text-muted-foreground hover:bg-muted/50 flex items-center justify-center"
            >
              −
            </button>
            <input
              type="number"
              value={shares}
              onChange={(e) => setShares(Math.max(1, parseInt(e.target.value) || 1))}
              className="flex-1 bg-muted/30 border border-border rounded-lg text-center text-foreground font-heading font-bold py-1.5 text-sm focus:outline-none focus:border-accent/50"
              min={1}
              max={mode === "buy" ? maxBuy : maxSell}
            />
            <button
              onClick={() => setShares(shares + 1)}
              className="w-8 h-8 rounded-lg border border-border text-muted-foreground hover:bg-muted/50 flex items-center justify-center"
            >
              +
            </button>
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-muted-foreground/50 text-[10px]">
              Max: {mode === "buy" ? maxBuy : maxSell}
            </span>
            <button
              onClick={() => setShares(mode === "buy" ? maxBuy : maxSell)}
              className="text-accent/60 text-[10px] hover:text-accent"
            >
              Set max
            </button>
          </div>
        </div>

        {/* Order summary */}
        <div className="bg-muted/30 rounded-xl border border-border/50 p-3 mb-5 space-y-1.5 text-xs">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Order Total</span>
            <span className="text-foreground font-heading font-bold">${total.toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Cash After</span>
            <span className={`font-heading font-bold ${mode === "buy" ? "text-foreground/70" : "text-emerald-400"}`}>
              ${(mode === "buy" ? cash - total : cash + total).toFixed(2)}
            </span>
          </div>
        </div>

        {/* Validation */}
        {mode === "buy" && total > cash && (
          <div className="flex items-center gap-2 mb-3 text-red-400 text-xs">
            <AlertCircle className="w-3.5 h-3.5" />
            <span>Insufficient cash</span>
          </div>
        )}
        {mode === "sell" && shares > maxSell && (
          <div className="flex items-center gap-2 mb-3 text-red-400 text-xs">
            <AlertCircle className="w-3.5 h-3.5" />
            <span>Not enough shares</span>
          </div>
        )}

        <Button
          onClick={handleTrade}
          disabled={
            loading ||
            shares <= 0 ||
            (mode === "buy" && total > cash) ||
            (mode === "sell" && shares > maxSell)
          }
          className={`w-full font-heading font-bold ${
            mode === "buy"
              ? "bg-emerald-400/20 border border-emerald-400/50 text-emerald-400 hover:bg-emerald-400/30"
              : "bg-red-400/20 border border-red-400/50 text-red-400 hover:bg-red-400/30"
          } disabled:opacity-40`}
        >
          {loading ? "Processing..." : mode === "buy" ? `Buy ${shares} Shares` : `Sell ${shares} Shares`}
        </Button>
      </div>
    </div>
  );
};

export default TradeModal;
